import { describeError } from '../errorMessage';
import {
    ensureBauordnung,
    fetchBauordnungBundle,
    fuehreAutomatikAus,
    meineFirma,
    updateOrdnung,
    type SchreibKontext,
} from '../bauordnungService';
import { fetchHandbuch } from '../handbuchService';
import { fetchNotrufplan } from '../asplanService';
import { baueSteckbrief, OHNE_LESEFEHLER, type ProjektSteckbrief } from '../projektSteckbrief/steckbrief';
import { ergaenzeHistorie } from '../../components/bauordnung/bauordnungLogic';
import {
    MERKMAL_KLAERUNG, VERWEIS_ERLASSENDER, baueAutomatikEntwurf, platzhalterAusVerweis, type AutomatikPaket,
} from '../../components/bauordnung/bausteine/automatik';
import { nameZuKurz } from '../../components/bauordnung/bausteine/platzhalter';
import { gezogeneKapitel } from '../../components/bauordnung/kapitel';
import { grundlagenVorschlaege, type GrundlagenVorschlag } from '../../components/bauordnung/vorschlaege';
import type { Bauordnung } from '../../components/bauordnung/types';
import type { BestandStand, ErzeugungsErgebnis, FolgeErzeuger, PlanungsQuelle } from './typen';

/**
 * Folgeunterlage „Baustellenordnung" — der Entwurf aus der Planungsphase.
 *
 * ═══ WAS HIER GESCHIEHT ═══════════════════════════════════════════════════
 * Ist die Planungsphase erfuellt, legt die App den Kopfsatz der
 * Baustellenordnung als ENTWURF an (`ensureBauordnung` — Katalogfassung,
 * Stand und Historie stimmen damit wie beim Knopf im Modul), wendet die
 * Vorschlaege der Leiste „Aus der Planungsphase" (`grundlagenVorschlaege`)
 * vollstaendig an, statt sie einzeln anklicken zu lassen, und laesst danach
 * den AUTOMATIK-LAUF folgen (Plan docs/baustellenordnung-assistenz-plan.md
 * §3.1 a, Stufe B4): Katalogbausteine als bestaetigte Regeln, Rollen aus den
 * Projektbeteiligten, offene Punkte fuer jeden Platzhalter ohne Projektdatum.
 * Der Kopfsatz-Patch zuerst:
 *
 *   Geltungsbereich  ← Projektadresse (wie der Knopf im Modul)
 *   Erlassender      ← Bauherr ODER Dritter nach § 4 BaustellV — nur wenn
 *                      genau EINER von beiden erfasst ist; stehen beide da,
 *                      ist das eine Vertragsfrage und bleibt offen
 *                      (`vorschlaege.ts`: „nicht einer vorgezogen")
 *   Gueltig von/bis  ← Ausfuehrungszeitraum (Entscheid E1)
 *
 * Geschrieben wird nur, was der Steckbrief als `belegt` fuehrt. Ein Wert aus
 * einer Ersatzquelle (Projektlaufzeit statt Ausfuehrungszeitraum) bleibt als
 * Satz in `offen` mit Vorschlag stehen — ein vermuteter Geltungszeitraum
 * stuende sonst als Tatsache im Dokument, und die Leiste im Modul zeigte ihn
 * nicht mehr an, weil das Feld schon so belegt waere.
 *
 * ═══ DER AUTOMATIK-LAUF ═══════════════════════════════════════════════════
 * Gerechnet in `bausteine/automatik.ts` (rein), geschrieben in EINER Folge
 * von `fuehreAutomatikAus`. Gelesen werden dafuer Buendel, Handbuch,
 * Notrufplan und die eigene Firma — die drei letzten fail-open: fehlt eines,
 * bleiben die betroffenen Platzhalter offene Punkte, der Lauf bricht nicht
 * ab. Scheitert der Lauf selbst, bleibt der Kopfsatz angelegt, `offen` nennt
 * den Fehler, und das naechste Oeffnen des Moduls holt ihn nach (der Marker
 * fehlt dann in der Historie).
 *
 * Die Uebernahme aus dem SiGe-Handbuch laeuft genau EINMAL. Die Automatik
 * loest sie nur aus, wenn das Handbuch Ordnungsfelder mit INHALT fuehrt —
 * ein eben erst angelegtes, leeres Handbuch verbraucht sie nicht: der Marker
 * `uebernommen_handbuch_am` bleibt dann leer, ein spaeteres Oeffnen holt die
 * Uebernahme nach (Entscheid E7).
 *
 * ═══ WAS HIER NICHT GESCHIEHT ═════════════════════════════════════════════
 *   - keine Freigabe, kein Einfrieren (`eingefroren_*` erst bei Freigabe),
 *     keine Buchung, keine Ablage, kein Versand, keine KI (die braucht die
 *     sichtbare Lesekarte im Modul, Plan B3);
 *   - keine Kapitelauswahl: die Kapitel der Baustellenordnung werden LIVE aus
 *     den Merkmalen gezogen (`gezogeneKapitel`), gespeichert wird keine
 *     Auswahl — die Zusatzkapitel des HSE-Plans bleiben bewusst unvorbelegt
 *     (GrundlagenReiter: „ein Kapitel, das niemand ausgewaehlt hat, wuerde
 *     sonst als geregelt gelten");
 *   - NIE ueberschreiben: gibt es schon einen Kopfsatz, bleibt er, wie er
 *     ist (`vorhanden`) — auch die Automatik laeuft dann nicht hier, sondern
 *     beim Oeffnen des Moduls. Innerhalb des eigenen Entwurfs werden nur
 *     leere Felder gefuellt (`ersetzt` leer).
 *
 * ═══ AUFBAU ══════════════════════════════════════════════════════════════
 * Die Saetze und der Patch entstehen pur (`baueBauordnungEntwurf`, getestet
 * ohne Datenbank); die Supabase-Aufrufe laufen ausschliesslich ueber
 * `bauordnungService` — der einzige Datenbankzugriff des Moduls 06 bleibt
 * der einzige.
 */

const ART = 'bauordnung' as const;

// ═══════════════════════════════════════════════════════════════════════════
// PUR — Steckbrief, Bestandstext, Entwurf
// ═══════════════════════════════════════════════════════════════════════════

/** Der Steckbrief aus den gespeicherten Staenden, die der Aufrufer mitgibt. */
export const steckbriefAusQuelle = (q: PlanungsQuelle): ProjektSteckbrief =>
    baueSteckbrief({
        metadata: q.projektDaten,
        phase: q.phase,
        fragen: q.fragen,
        // Die Mitgeltenden des SiGe-Plans steuern nur die Unterlagen-
        // Checkliste — die Baustellenordnung liest sie nicht.
        sigeSchritte: [],
        lesefehler: { ...OHNE_LESEFEHLER },
    }, q.heuteIso);

/**
 * Tag als TT.MM.JJJJ. Ein reiner ISO-Tag wird zerlegt; ein Zeitstempel
 * (`freigegeben_am`) in Ortszeit gelesen, sonst stuende eine Freigabe kurz
 * nach Mitternacht am Vortag.
 */
const tagDeutsch = (wert: string | null | undefined): string => {
    const s = String(wert || '').trim();
    if (!s) return '';
    const tag = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (tag) return `${tag[3]}.${tag[2]}.${tag[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

/** Kurzbeschreibung des Bestands — „Entwurf vom 12.10.2026", „Freigegeben am 14.10.2026 (Rev. 01)". */
export const bauordnungBestandText = (
    o: Pick<Bauordnung, 'status' | 'stand' | 'freigegeben_am' | 'version_label'>,
): string => {
    if (o.status === 'freigegeben') {
        const am = tagDeutsch(o.freigegeben_am);
        const fassung = String(o.version_label || '').trim();
        return `Freigegeben${am ? ` am ${am}` : ''}${fassung ? ` (${fassung})` : ''}`;
    }
    const stand = tagDeutsch(o.stand);
    return stand ? `Entwurf vom ${stand}` : 'Entwurf ohne Stand';
};

/**
 * Traegt der Kopfsatz noch nichts ausser dem, was `ensureBauordnung` beim
 * Anlegen schreibt? Nur dann fuellt der Erzeuger ihn. `ensureBauordnung`
 * gibt einen VORHANDENEN Kopfsatz kommentarlos zurueck — legte eine zweite
 * Sitzung ihn zwischen Pruefung und Anlegen an und hat schon etwas
 * eingetragen, ist er nicht unserer und bleibt unberuehrt.
 */
export const istUnberuehrt = (o: Bauordnung): boolean =>
    o.status === 'entwurf'
    && (o.historie || []).length <= 1
    && !o.erlassender_firma.trim()
    && !o.erlassender_name.trim()
    // Die Rolle setzt der Grundlagen-Reiter auch allein (ohne Firma und ohne
    // Historieneintrag) — eine gesetzte Rolle heisst: jemand hat schon gewaehlt.
    && !String(o.erlassender_rolle || '').trim()
    && !o.geltung_von
    && !o.geltung_bis
    && !o.uebernommen_handbuch_am;

/** Was beim Anlegen mitgeschrieben wird — der Geltungsbereich, wie beim Knopf im Modul. */
export const vorbelegungAus = (sb: ProjektSteckbrief): { geltung_ort: string } => {
    const adresse = sb.projekt.adresse;
    return { geltung_ort: adresse.stand === 'belegt' ? (adresse.wert || '').trim() : '' };
};

export interface BauordnungEntwurf {
    /** Aenderung am Kopfsatz — leer, wenn nichts zu schreiben ist. Traegt dann auch keine Historie. */
    patch: Partial<Bauordnung>;
    /** Saetze, die unabhaengig vom Patch gelten (Anlage, Geltungsbereich, Kapitel). */
    uebernommen: string[];
    /** Saetze, die nur gelten, wenn der Patch geschrieben wurde. */
    ausPatch: string[];
    offen: string[];
}

const HERKUNFT_BAUHERR = 'aus dem Bauherrn';
const HERKUNFT_DRITTER = 'aus dem Dritten nach § 4 BaustellV';

const GELTUNG: ReadonlyArray<{ feld: 'geltung_von' | 'geltung_bis'; label: string }> = [
    { feld: 'geltung_von', label: 'Gültig von' },
    { feld: 'geltung_bis', label: 'Gültig bis' },
];

/**
 * Der Entwurf zu einem frisch angelegten Kopfsatz — pur.
 *
 * `jetztIso` ist der Zeitstempel des Historieneintrags (vom Aufrufer, kein
 * `new Date()` hier), `bearbeiter` steht als Handelnder darin.
 */
export const baueBauordnungEntwurf = (
    sb: ProjektSteckbrief,
    o: Bauordnung,
    opt: { jetztIso: string; bearbeiter: string },
): BauordnungEntwurf => {
    const patch: Partial<Bauordnung> = {};
    const felder: string[] = [];
    const uebernommen: string[] = ['Entwurf der Baustellenordnung angelegt.'];
    const ausPatch: string[] = [];
    const offen: string[] = [];

    // NIE ersetzen: ein Vorschlag mit `ersetzt` trifft ein schon gefuelltes
    // Feld — das entscheidet der Mensch in der Leiste, nicht die Automatik.
    const vorschlaege = grundlagenVorschlaege(sb, o).filter(v => !v.ersetzt);

    // ─── Geltungsbereich (beim Anlegen geschrieben) ──────────────────────
    if (o.geltung_ort.trim()) {
        uebernommen.push(`Geltungsbereich aus der Projektadresse übernommen: ${o.geltung_ort.trim()}.`);
    } else {
        offen.push(`Geltungsbereich eintragen: ${sb.projekt.adresse.grund || 'Die Projektadresse ist nicht gepflegt.'}`);
    }

    // ─── Erlassender ─────────────────────────────────────────────────────
    if (!o.erlassender_firma.trim()) {
        const erlassende = vorschlaege.filter(v => v.feld === 'erlassender');
        const belegte = erlassende.filter(v => v.stand === 'belegt');
        if (belegte.length === 1) {
            const v = belegte[0];
            Object.assign(patch, v.patch);
            felder.push('Erlassender');
            const herkunft = v.id === 'erlassender_dritter' ? HERKUNFT_DRITTER : HERKUNFT_BAUHERR;
            ausPatch.push(`Erlassender ${herkunft} übernommen: ${v.wertText}.`);
            if (!v.patch.erlassender_rolle) {
                const rolle = (sb.dritter.rolle.wert || '').trim();
                offen.push(rolle
                    ? `Rolle des Erlassenden wählen: „${rolle}“ lässt sich keiner Rolle eindeutig zuordnen.`
                    : 'Rolle des Erlassenden wählen: Zum Dritten nach § 4 BaustellV ist keine Rolle erfasst.');
            }
        } else if (belegte.length > 1) {
            const bauherr = belegte.find(v => v.id === 'erlassender_bauherr');
            const dritter = belegte.find(v => v.id === 'erlassender_dritter');
            offen.push(`Erlassenden wählen: Bauherr „${bauherr?.wertText ?? ''}“ oder „${dritter?.wertText ?? ''}“ `
                + 'als Dritter nach § 4 BaustellV — das regelt der Vertrag.');
        } else if (erlassende.length) {
            const v = erlassende[0];
            offen.push(`Erlassenden prüfen und eintragen: ${v.grund || 'Herkunft nicht geklärt.'} Vorschlag: ${v.wertText}.`);
        } else {
            offen.push(`Erlassenden (Firma) eintragen: ${sb.bauherr.name.grund || 'Kein Bauherr erfasst.'}`);
        }
    }
    if (!o.erlassender_name.trim()) {
        offen.push('Unterzeichner des Erlassenden eintragen — ohne ihn ist keine Freigabe möglich.');
    }

    // ─── Geltungszeitraum ────────────────────────────────────────────────
    for (const { feld, label } of GELTUNG) {
        if (o[feld]) continue;
        const v: GrundlagenVorschlag | undefined = vorschlaege.find(x => x.feld === feld);
        if (v && v.stand === 'belegt') {
            Object.assign(patch, v.patch);
            felder.push(label);
            ausPatch.push(`${label} ${v.wertText} übernommen (${v.quelle}).`);
        } else if (v) {
            offen.push(`${label} prüfen und eintragen: ${v.grund || 'Herkunft nicht geklärt.'} Vorschlag: ${v.wertText}.`);
        } else {
            const angabe = feld === 'geltung_von' ? sb.ausfuehrung.beginn : sb.ausfuehrung.ende;
            offen.push(`${label} eintragen: ${angabe.grund || 'Der Ausführungszeitraum ist nicht gepflegt.'}`);
        }
    }

    // ─── Kapitel: live aus den Merkmalen, nichts zu speichern ────────────
    const gezogen = gezogeneKapitel('ordnung', sb.merkmale).length;
    const moeglich = gezogeneKapitel('ordnung', {}).length;
    uebernommen.push(`Kapitel nach den Merkmalen der Planungsphase: ${gezogen} von ${moeglich} gezogen.`);

    // Organisation, Kapiteltexte und Handbuch nennt `automatikSaetze` — sie
    // haengen davon ab, was der Automatik-Lauf danach geschrieben hat.

    if (felder.length) {
        patch.historie = ergaenzeHistorie(
            o.historie,
            opt.bearbeiter,
            `Aus der Planungsphase vorbelegt: ${felder.join(', ')}`,
            opt.jetztIso,
        );
    }

    return { patch, uebernommen, ausPatch, offen };
};

/** Was ohne Automatik ein Mensch erledigt — die Saetze, wenn der Lauf nicht kam. */
export const OHNE_AUTOMATIK: readonly string[] = [
    'Organisation eintragen: Rollen, Ansprechpartner und Erreichbarkeit auf der Baustelle.',
    'Kapiteltexte wählen und bestätigen — erst bestätigte Regelungen stehen im Dokument.',
];

const anzahl = (n: number, eins: string, viele: string): string => `${n} ${n === 1 ? eins : viele}`;

/** Hoechstens so viele Namen in einem Satz — die Liste steht vollstaendig im Modul. */
const MAX_NAMEN = 8;
const aufzaehlung = (namen: string[]): string =>
    namen.length > MAX_NAMEN ? `${namen.slice(0, MAX_NAMEN).join(', ')}, …` : namen.join(', ');

/**
 * Die Saetze zum Automatik-Lauf — pur, aus dem geschriebenen Paket.
 *
 * Gezaehlt wird, was geschrieben wurde; die offenen Punkte werden je Frage
 * einmal genannt (derselbe Platzhalter in drei Regeln ist EINE Frage, auch
 * wenn er drei Punkte traegt).
 */
export const automatikSaetze = (p: AutomatikPaket): { uebernommen: string[]; offen: string[] } => {
    const uebernommen: string[] = [];
    const offen: string[] = [];

    if (p.regeln.length) {
        uebernommen.push(`${anzahl(p.regeln.length, 'Regelung', 'Regelungen')} aus dem Katalog bestätigt.`);
    } else {
        offen.push(OHNE_AUTOMATIK[1]);
    }
    if (p.handbuch?.regeln.length) {
        uebernommen.push(`${anzahl(p.handbuch.regeln.length, 'Zeile', 'Zeilen')} aus dem SiGe-Handbuch übernommen und bestätigt.`);
    } else {
        offen.push('Übernahme aus dem SiGe-Handbuch steht aus — sie läuft beim Öffnen des Moduls von selbst, '
            + 'sobald das Handbuch Arbeitszeiten, Zugang, Regeln oder Notfall führt.');
    }
    if (p.rollen.length) {
        uebernommen.push(`${anzahl(p.rollen.length, 'Rolle', 'Rollen')} angelegt: ${p.rollen.map(r => r.rolle).join(', ')}.`);
    } else {
        offen.push(OHNE_AUTOMATIK[0]);
    }

    const punkte = p.luecken.filter(l => !l.merkmal);
    if (punkte.length) {
        const namen = Array.from(new Set(punkte.map(l => {
            if (l.verweis === VERWEIS_ERLASSENDER) return 'Erlassender';
            const name = platzhalterAusVerweis(l.verweis);
            return name ? nameZuKurz(name) : l.punkt;
        })));
        offen.push(`${anzahl(punkte.length, 'offener Punkt', 'offene Punkte')}: ${aufzaehlung(namen)}.`);
    }
    if (p.klaeren.length) {
        const namen = p.klaeren.map(k => MERKMAL_KLAERUNG[k.merkmal]?.kurz ?? k.merkmal);
        offen.push(`${anzahl(p.klaeren.length, 'Merkmal', 'Merkmale')} zu klären: ${aufzaehlung(namen)}.`);
    }
    return { uebernommen, offen };
};

// ═══════════════════════════════════════════════════════════════════════════
// DUENN — Lesen und Schreiben ueber bauordnungService
// ═══════════════════════════════════════════════════════════════════════════

const SCHEMA_FEHLT = 'Die Tabellen der Baustellenordnung fehlen in dieser Datenbank (Migration Nr. 43).';
const OHNE_PROJEKT = 'Ohne gespeichertes Projekt kann keine Baustellenordnung angelegt werden.';

type Lage =
    | { status: 'keiner' }
    | { status: 'vorhanden'; ordnung: Bauordnung }
    | { status: 'fehler'; fehler: string };

/** Liest den Kopfsatz — NUR lesen (`fetchBauordnungBundle` legt nichts an). */
const leseLage = async (projectId: string): Promise<Lage> => {
    try {
        const bundle = await fetchBauordnungBundle(projectId);
        // Lesen ist dort fail-open: eine fehlende Tabelle kommt als Flagge,
        // nicht als Fehler. Fuer den Erzeuger ist das KEIN „keiner" — das
        // Anlegen scheiterte sonst erst beim Schreiben.
        if (bundle.schemaFehlt) return { status: 'fehler', fehler: SCHEMA_FEHLT };
        return bundle.ordnung ? { status: 'vorhanden', ordnung: bundle.ordnung } : { status: 'keiner' };
    } catch (e) {
        return { status: 'fehler', fehler: `Die Baustellenordnung konnte nicht gelesen werden: ${describeError(e)}` };
    }
};

/** Lesen ohne Abbruch: ein Fehler heisst hier nur „diese Quelle fuellt nichts". */
const failOpen = async <T>(lesen: () => Promise<T | null>): Promise<T | null> => {
    try {
        return await lesen();
    } catch {
        return null;
    }
};

const belegtOderLeer = (a: { wert: string | null; stand: string }): string =>
    a.stand === 'belegt' ? String(a.wert || '').trim() : '';

/**
 * Der Automatik-Lauf nach dem Kopfsatz. Wirft nie: ein Fehler wird zum Satz
 * in `offen`, der Kopfsatz bleibt angelegt, und das Modul holt den Lauf beim
 * naechsten Oeffnen nach (der Marker fehlt dann).
 */
const automatikLauf = async (
    q: PlanungsQuelle,
    sb: ProjektSteckbrief,
    ctx: SchreibKontext,
): Promise<{ uebernommen: string[]; offen: string[] }> => {
    try {
        const bundle = await fetchBauordnungBundle(q.projectId);
        if (bundle.schemaFehlt) throw new Error(SCHEMA_FEHLT);
        const ordnung = bundle.ordnung;
        if (!ordnung) throw new Error('Der eben angelegte Kopfsatz ist nicht lesbar.');

        const [handbuch, notrufplan, firma] = await Promise.all([
            failOpen(() => fetchHandbuch(q.projectId)),
            failOpen(() => fetchNotrufplan(q.projectId)),
            failOpen(() => meineFirma()),
        ]);
        const paket = baueAutomatikEntwurf({
            ordnung,
            bundle,
            merkmale: sb.merkmale,
            steckbrief: sb,
            handbuch,
            notrufplan,
            buero: { firma: firma?.company_name ?? '', handelnder: q.bearbeiter },
            projekt: { name: belegtOderLeer(sb.projekt.name), adresse: belegtOderLeer(sb.projekt.adresse) },
            heuteIso: q.heuteIso,
        });
        await fuehreAutomatikAus(ordnung, paket, { ...ctx, status: 'entwurf' });
        return automatikSaetze(paket);
    } catch (e) {
        return {
            uebernommen: [],
            offen: [
                `Automatik nicht gelaufen: ${describeError(e)} — beim Öffnen des Moduls wird sie nachgeholt.`,
                ...OHNE_AUTOMATIK,
            ],
        };
    }
};

const bestand = async (projectId: string): Promise<BestandStand> => {
    if (!projectId || projectId === 'temp-id') return { status: 'keiner' };
    const lage = await leseLage(projectId);
    if (lage.status === 'vorhanden') return { status: 'vorhanden', beschreibung: bauordnungBestandText(lage.ordnung) };
    return lage;
};

const legeAn = async (q: PlanungsQuelle): Promise<ErzeugungsErgebnis> => {
    if (!q.projectId || q.projectId === 'temp-id') return { status: 'fehler', art: ART, fehler: OHNE_PROJEKT };

    // Unmittelbar vor dem Schreiben noch einmal: gibt es ihn, bleibt er.
    const lage = await leseLage(q.projectId);
    if (lage.status === 'fehler') {
        return { status: 'fehler', art: ART, fehler: `Keine Baustellenordnung angelegt. ${lage.fehler}` };
    }
    if (lage.status === 'vorhanden') {
        return { status: 'vorhanden', art: ART, beschreibung: bauordnungBestandText(lage.ordnung) };
    }

    const sb = steckbriefAusQuelle(q);
    // Den Nur-Ansicht-Schutz traegt der Aufrufer (die Planungsphase legt nur
    // in einem beschreibbaren Projekt an); der Vertrag reicht ihn nicht mit.
    // Die RLS der Tabellen bleibt davon unberuehrt.
    const ctx: SchreibKontext = { nurAnsicht: false, handelnder: q.bearbeiter };

    let ordnung: Bauordnung;
    try {
        ordnung = await ensureBauordnung(q.projectId, ctx, vorbelegungAus(sb));
    } catch (e) {
        // Legte eine zweite Sitzung den Kopfsatz eben an, scheitert das
        // Einfuegen am Unique-Index `(project_id)` — dann gibt es ihn, und er
        // bleibt, wie er ist.
        const danach = await leseLage(q.projectId);
        if (danach.status === 'vorhanden') {
            return { status: 'vorhanden', art: ART, beschreibung: bauordnungBestandText(danach.ordnung) };
        }
        return { status: 'fehler', art: ART, fehler: `Die Baustellenordnung konnte nicht angelegt werden: ${describeError(e)}` };
    }
    if (!istUnberuehrt(ordnung)) {
        return { status: 'vorhanden', art: ART, beschreibung: bauordnungBestandText(ordnung) };
    }

    const entwurf = baueBauordnungEntwurf(sb, ordnung, { jetztIso: new Date().toISOString(), bearbeiter: q.bearbeiter });
    const offen = [...entwurf.offen];
    let patchGeschrieben = true;
    if (Object.keys(entwurf.patch).length) {
        try {
            await updateOrdnung(ordnung.id, entwurf.patch, { ...ctx, status: 'entwurf' });
        } catch (e) {
            patchGeschrieben = false;
            offen.unshift(`Erlassender und Geltungszeitraum wurden nicht eingetragen: ${describeError(e)} `
                + 'Bitte in den Grundlagen über die Leiste „Aus der Planungsphase“ übernehmen.');
        }
    }

    // Der Lauf liest den Kopfsatz frisch — mit oder ohne Patch, je nachdem,
    // was eben angekommen ist. Ohne Patch fuellt er die leeren Felder selbst
    // aus dem Steckbrief (nur `belegt`), mehr nicht.
    const automatik = await automatikLauf(q, sb, ctx);

    return {
        status: 'angelegt',
        art: ART,
        uebernommen: [...entwurf.uebernommen, ...(patchGeschrieben ? entwurf.ausPatch : []), ...automatik.uebernommen],
        offen: [...offen, ...automatik.offen],
    };
};

export const bauordnungErzeuger: FolgeErzeuger = {
    art: ART,
    titel: 'Baustellenordnung',
    bestand,
    legeAn,
};
